import React from 'react';
import clsx from 'clsx';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import {
  Check,
  ChevronDown,
  Columns2,
  Columns3,
  Columns4,
  EllipsisVertical,
  GripVertical,
  Plus,
  Square,
  SquareDashed,
} from 'lucide-react';
import { FORM_LAYOUT_COLUMNS, layoutItemKey } from '@repo/lib';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import { Button as ShadcnButton, buttonVariants } from '@dashboard/components/ui/button';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@dashboard/components/ui/accordion';
import { FormElement, Label, LabelDescription } from '@dashboard/components/Form';
import { Formik } from 'formik';
import { FormSubmit, TextInput } from '@dashboard/components/Formik';
import { useFormLayout } from '../FormLayoutContext';
import { staticStrategy } from './fieldsDrag';
import PickerMenuItems from './AddPicker/PickerMenuItems';
import { PICKER_MENU_CLASS } from './AddPicker/pickerItems';

const COLUMN_ICONS = { 1: Square, 2: Columns2, 3: Columns3, 4: Columns4 };

export const columnsLabel = columns => `${columns} column${columns === 1 ? '' : 's'}`;

export function ColumnsMenu({ group, disabled }) {
  const { setColumns } = useFormLayout();
  const ColumnsIcon = COLUMN_ICONS[group.columns] ?? Columns2;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type="button"
          aria-label={`Columns: ${columnsLabel(group.columns)}`}
          title={columnsLabel(group.columns)}
          className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-muted-foreground hover:bg-muted hover:text-foreground data-[state=open]:bg-muted"
        >
          <ColumnsIcon className="size-3.5" aria-hidden="true" />
          <span className="tabular-nums">{group.columns}</span>
          <ChevronDown className="size-3" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-40">
        {FORM_LAYOUT_COLUMNS.map(columns => (
          <DropdownMenuItem key={columns} onSelect={() => setColumns(group.id, columns)}>
            {columnsLabel(columns)}
            {group.columns === columns && <Check className="ml-auto size-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function GroupNameEditor({ group, onClose }) {
  const { renameGroup } = useFormLayout();
  const inputId = `group:${group.id}_name`;

  return (
    <Formik
      enableReinitialize
      initialValues={{ name: group.name ?? '' }}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={async (values, { setSubmitting }) => {
        await renameGroup(group.id, values.name);
        setSubmitting(false);
      }}
    >
      {({ handleSubmit, dirty, isSubmitting, resetForm }) => (
        <form onSubmit={handleSubmit}>
          <FormElement>
            <Label for={inputId}>Group name</Label>
            <TextInput id={inputId} name="name" type="text" placeholder="Optional" clearable />
            <LabelDescription className="mt-1 mb-0">
              Name this group to help organize your fields. Only visible to you.
            </LabelDescription>
          </FormElement>
          <FormSubmit align="end">
            <FormSubmit.State visible={!!dirty} />
            <FormSubmit.Cancel
              onClick={() => {
                resetForm();
                onClose?.();
              }}
            >
              Cancel
            </FormSubmit.Cancel>
            <FormSubmit.Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
              Save
            </FormSubmit.Button>
          </FormSubmit>
        </form>
      )}
    </Formik>
  );
}

export default function GroupCard({
  group,
  dragHandleProps,
  isDropTarget,
  into,
  disabled,
  onAddToGroup,
  addMenu,
  onClose,
  renderChild,
}) {
  const { ungroup, duplicateGroup, deleteGroup } = useFormLayout();
  const { setNodeRef } = useDroppable({
    id: `container:${group.id}`,
    data: { containerId: group.id },
  });
  const value = `group:${group.id}`;
  const childKeys = group.items.map(layoutItemKey);

  return (
    <div
      ref={setNodeRef}
      data-testid="field-group"
      data-group-id={group.id}
      data-drop={into ? 'into' : undefined}
      className={clsx('my-2.5 overflow-hidden rounded-lg border bg-white dark:bg-background', {
        'border-gray-200 dark:border-border': !isDropTarget,
        'border-foreground ring-1 ring-foreground': isDropTarget,
      })}
    >
      <AccordionItem value={value} className="border-b-0">
        <div className="flex flex-row items-center gap-2 px-1 border-b border-gray-200 dark:border-border">
          <div
            {...dragHandleProps}
            className={clsx(
              buttonVariants({ variant: 'ghost', size: 'icon' }),
              'cursor-grab active:cursor-grabbing',
            )}
          >
            <GripVertical className="size-4" />
          </div>
          <div className="flex min-w-0 flex-1 items-center [&>h3]:min-w-0 [&>h3]:flex-1">
            <AccordionTrigger
              className="py-3 min-w-0 flex-1 items-center hover:no-underline [&>span:last-child]:hidden"
              aria-label={group.name ? `Edit ${group.name}` : 'Edit group'}
            >
              <div className="flex min-w-0 items-center gap-x-2">
                <SquareDashed className="size-4 shrink-0" />
                {group.name ? (
                  <span className="truncate font-semibold">{group.name}</span>
                ) : (
                  <span className="font-medium text-muted-foreground">Group</span>
                )}
              </div>
            </AccordionTrigger>
          </div>
          <ColumnsMenu group={group} disabled={disabled} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={disabled}>
              <ShadcnButton
                variant="ghost"
                size="icon"
                aria-label="Group options"
                disabled={disabled}
              >
                <EllipsisVertical className="size-4" />
              </ShadcnButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {addMenu ? (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>Add to group</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className={PICKER_MENU_CLASS}>
                    <PickerMenuItems sections={addMenu.sections} onPick={addMenu.onPick} />
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              ) : (
                <DropdownMenuItem onSelect={() => onAddToGroup(group.id)}>
                  Add to group
                </DropdownMenuItem>
              )}
              {duplicateGroup && (
                <DropdownMenuItem onSelect={() => duplicateGroup(group.id)}>
                  Duplicate
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {/* Ungrouping an empty group is deleting it, so only one of the two shows. */}
              {group.items.length > 0 ? (
                <DropdownMenuItem onSelect={() => ungroup(group.id)}>Ungroup</DropdownMenuItem>
              ) : (
                <DropdownMenuItem variant="destructive" onSelect={() => deleteGroup(group.id)}>
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <AccordionContent className="px-3 pt-2 pb-4">
          <GroupNameEditor group={group} onClose={onClose} />
        </AccordionContent>
      </AccordionItem>

      <div className={clsx({ 'opacity-40': into })}>
        <SortableContext id={group.id} items={childKeys} strategy={staticStrategy}>
          {group.items.map(child => renderChild(child, group.id))}
        </SortableContext>
        {group.items.length === 0 && (
          <div className="mx-2.5 my-2 rounded-md border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">
            Drag fields here
          </div>
        )}
      </div>
      <div className="px-1 py-1">
        {addMenu ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={disabled}>
              <ShadcnButton
                type="button"
                variant="ghost"
                className="h-9 gap-2 pl-0 pr-3 font-normal text-muted-foreground"
                disabled={disabled}
                onClick={addMenu ? undefined : () => onAddToGroup(group.id)}
              >
                <span className="flex size-9 items-center justify-center">
                  <Plus className="size-4" />
                </span>
                Add to group
              </ShadcnButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className={PICKER_MENU_CLASS}>
              <PickerMenuItems sections={addMenu.sections} onPick={addMenu.onPick} />
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <ShadcnButton
            type="button"
            variant="ghost"
            className="h-9 gap-2 pl-0 pr-3 font-normal text-muted-foreground"
            disabled={disabled}
            onClick={addMenu ? undefined : () => onAddToGroup(group.id)}
          >
            <span className="flex size-9 items-center justify-center">
              <Plus className="size-4" />
            </span>
            Add to group
          </ShadcnButton>
        )}
      </div>
    </div>
  );
}
